import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { AllLabels, InstallationFlowType } from '../../../../constants/app.constants';
import { AllDropdownKeys, ZerezIdMask } from '../../../jurisdiction.constants';
import { TechnicianDataService } from '../../technician-data.service';
import { KeyValueData } from '../../../../domains/common.domain';
import { LookupOptionsData } from '../../../jurisdiction.domain';
import { CommonService } from '../../../../services/common.service';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { ZerezService } from '../../../../services/zerez.service';
import { DestroyableComponent } from '../../../../common/destroyable.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorService } from '../../../../services/error/error.service';
import { ToastNotificationService } from '../../../../+shared/services/toast-notification.service';
import { ValidationsService } from '../../../../services/validations.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { SpecialConsumerDeviceTypeChange } from '../../technician-data.domain';

@Component({
  providers: [ZerezService],
  selector: 'app-special-consumers',
  styleUrls: ['./special-consumers.component.scss'],
  templateUrl: './special-consumers.component.html',
})
export class SpecialConsumersComponent extends DestroyableComponent implements OnChanges, OnInit {
  private static tempKeyCounter = 0;

  @Input() specialDeviceData: any = null;
  @Output() deleteComponent: EventEmitter<number>;
  @Input() isReadOnly: boolean = false;
  @Output() deviceTypeChange: EventEmitter<SpecialConsumerDeviceTypeChange> = new EventEmitter<SpecialConsumerDeviceTypeChange>();

  componentIndex: number;
  currentInstallationFlow: string;
  installationFlowType: KeyValueData;
  specialDeviceKey: string;
  specialDeviceType: string;
  specialDeviceFG: FormGroup;
  specialDevices: LookupOptionsData[];
  unitLabels: KeyValueData;
  isLoading: boolean = false;
  energyProducerTypesAllowedForZerezApi = ['windturbine', 'other', 'pv_device_consumer', 'bhkw_kwk_device_consumer', 'storage_consumer'];
  energyProducerTypesAllowedForModule = ['heatpump', 'charger', 'wallbox', 'other', 'storage_consumer'];
  zerezIdMask = ZerezIdMask;
  readonly tempKey = `new_${Date.now()}_${SpecialConsumersComponent.tempKeyCounter++}`;

  get specialApplianceTypeNotOther(): boolean {
    return this.specialDeviceType != 'other';
  }

  constructor(
    private zerezService: ZerezService,
    private errorService: ErrorService,
    private commonService: CommonService,
    private toaster: ToastNotificationService,
    private localStorageService: LocalStorageService,
    private technicianDataService: TechnicianDataService,
    private environmentService: EnvironmentService
  ) {
    super();
    this.currentInstallationFlow = this.localStorageService.getItem('installation_flow');
    this.deleteComponent = new EventEmitter<number>();
  }

  ngOnInit(): void {
    this.specialDeviceKey =
      this.currentInstallationFlow === InstallationFlowType.TemporaryConnection ||
      this.currentInstallationFlow === InstallationFlowType.ChangeHouseConnection
        ? AllDropdownKeys.TempSpecialConsumerType
        : AllDropdownKeys.SpecialConsumerType;
    this.installationFlowType = InstallationFlowType;

    this.specialDevices = this.commonService.getDropdownData(this.specialDeviceKey);
    this.unitLabels = AllLabels.units;

    this.initializeData();

    this.technicianDataService.registerForActionOnFormSubmit.subscribe((value: boolean) => {
      if (value) {
        this.resetData();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.specialDeviceData && !changes.specialDeviceData.firstChange) {
      this.initializeData();
    }
  }

  selectSpecialDevice(event: any = null): void {
    if (event) {
      const previousType = this.specialDeviceType;
      this.specialDeviceType = event.value;

      if (previousType !== this.specialDeviceType) {
        this.deviceTypeChange.emit({
          consumerId: this.specialDeviceFG?.get('id')?.value || null,
          tempKey: this.tempKey,
          previousType: previousType || null,
          newType: this.specialDeviceType,
        });
      }
    }
    if (this.specialDeviceType === 'other') {
      this.specialDeviceFG.addControl('otherType', new FormControl(this.specialDeviceData?.otherType || '', [Validators.required]));
      this.specialDeviceFG.controls.otherType.markAsTouched();
    } else {
      this.specialDeviceFG.removeControl('otherType');
    }
    this.showModuleTypeAndZerezId();
  }

  removeElement(): void {
    this.deleteComponent.emit(this.componentIndex);
  }

  validateZerezId() {
    this.isLoading = true;
    const zerezId = this.specialDeviceFG.get('zerez_id').value;

    this.preventLeak(this.zerezService.getUnitByZerezId(zerezId)).subscribe({
      next: res => this.validateZerezResponse(res),
      error: err => this.validateZerezResponse(err),
    });
  }

  private validateZerezResponse(response: any) {
    // failed
    if (response instanceof HttpErrorResponse) {
      this.specialDeviceFG.get('zerez_id_validated').setValue(null); //'false' is considered as a valid value for a FormControl
      this.errorService.throwErrorIfFailure(response, true);
    }
    // successfull
    else {
      this.specialDeviceFG.get('zerez_id_validated').setValue(true);
      this.toaster.success('Zerez unit validated', 4000);
    }
    this.isLoading = false;
  }

  private showModuleTypeAndZerezId(): void {
    if (this.currentInstallationFlow === this.installationFlowType.TemporaryConnection) return;
    const isEligibleForZerezApi = this.energyProducerTypesAllowedForZerezApi.includes(this.specialDeviceType);
    const isEligibleForModule = this.energyProducerTypesAllowedForModule.includes(this.specialDeviceType);
    const validatorsList = this.specialApplianceTypeNotOther ? [Validators.required] : [];
    const isPowerSufficient = this.specialDeviceFG?.controls?.power?.value > 4.2;

    ['zerez_id', 'zerez_id_validated', 'module_type'].forEach(control => this.specialDeviceFG.removeControl(control));

    // Handle module_type control
    if (isPowerSufficient && isEligibleForModule) {
      this.addSpecialApplianceFGControl('module_type', this.specialDeviceData?.module_type, validatorsList);
    }

    if (!this.environmentService.hideZerezId && isEligibleForZerezApi) {
      // Handle zerez_id controls
      this.addSpecialApplianceFGControl('zerez_id', this.specialDeviceData?.zerez_id || '', [
        ValidationsService.zerezIdValidator(),
        ...validatorsList,
      ]);

      this.addSpecialApplianceFGControl('zerez_id_validated', this.specialDeviceData?.zerez_id ? true : null, validatorsList);

      // Setup zerez_id validation subscriptions
      this.specialDeviceFG.controls.zerez_id.valueChanges.subscribe(() => {
        this.specialDeviceFG.get('zerez_id_validated').setValue(null);
      });
    }
  }

  // Helper method to reduce repetition
  private addSpecialApplianceFGControl(controlName: string, value: any, validators: any[]): void {
    this.specialDeviceFG.addControl(controlName, new FormControl(value, validators));
  }

  private prepareForm(): void {
    const selectedDevice =
      (this.specialDeviceData &&
        this.specialDeviceData.device &&
        this.specialDevices.filter(item => item.value === this.specialDeviceData.device)[0]) ||
      null;
    this.specialDeviceType = (selectedDevice && selectedDevice.value) || '';

    this.specialDeviceFG = new FormGroup({
      device: new FormControl(this.specialDeviceType, [Validators.required]),
      count: new FormControl(this.specialDeviceData?.count || '', [Validators.required]),
      power: new FormControl(this.specialDeviceData?.power || '', [Validators.required]),
      id: new FormControl(this.specialDeviceData?.id || ''),
      tempKey: new FormControl(this.tempKey),
    });

    if (this.currentInstallationFlow !== this.installationFlowType.TemporaryConnection) {
      this.specialDeviceFG.addControl('description', new FormControl((this.specialDeviceData && this.specialDeviceData.description) || ''));
      this.specialDeviceFG.addControl(
        'is_controllable_facility',
        new FormControl(this.specialDeviceData?.is_controllable_facility, [Validators.required])
      );
      this.specialDeviceFG.addControl(
        'is_public_facility',
        new FormControl(this.specialDeviceData?.is_public_facility, [Validators.required])
      );
      this.specialDeviceFG.controls.power.valueChanges.subscribe(() => {
        this.showModuleTypeAndZerezId();
      });
    }

    if (this.specialDeviceType) {
      this.selectSpecialDevice();
    }
  }

  private initializeData(): void {
    this.prepareForm();

    this.componentIndex = this.technicianDataService.specialDeviceCount;
    this.technicianDataService.manageSpecialConsumersFormData({
      component: 'specialDevice',
      formData: this.specialDeviceFG,
      index: this.componentIndex,
    });
  }

  private resetData(): void {
    this.componentIndex = 0;
    this.specialDeviceType = '';
  }
}
